'use client';

import { useState } from 'react';

import type { Offer } from '@libs/common-types';
import {
    useLoyaltyContext,
    useAlertBanner,
    logSentryError,
    SentryAcpTreatsRewardsErrors as SentryErrors,
} from '@libs/common-next';
import { activateOffer } from '@libs/api-proxy-client/v2';
import { SnackbarUtils } from '@libs/shared-ui/utils';

export function getCardSize(offer?: Offer, isFeatured?: boolean) {
    if (isFeatured) {
        return 'featured';
    }

    return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}

export function useOfferCardActions() {
    const { drawerOpen, getOffers, isActivating, selectedOffer, setDrawerOpen, setSelectedOffer } = useLoyaltyContext();

    const { setAlert } = useAlertBanner();

    const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());

    function handleCardClick(offer: Offer) {
        setDrawerOpen(true);
        setSelectedOffer(offer);

        // Reset error so pre-existing error doesn't display inside offer drawer
        setAlert(undefined);
    }

    function handleCloseDrawer() {
        setDrawerOpen(false);
        setSelectedOffer(undefined);

        // Reset error so error from offer drawer doesn't display on offer grid
        setAlert(undefined);
    }

    async function handleActivateOffer(offer?: Offer, _gtmSection?: string) {
        if (offer?.isActivatable && !offer.isActivated) {
            setActivatingOffers(prevOffers => new Set(prevOffers).add(offer.offerId));

            try {
                const response = await activateOffer(offer);

                if (response && 'success' in response && response?.success) {
                    setAlert(undefined);

                    // Update selectedOffer immediately to reflect activated state
                    if (selectedOffer && selectedOffer.offerId === offer.offerId) {
                        setSelectedOffer({ ...selectedOffer, isActivated: true });
                    }
                    // Fetch offers again from API to get updated data after activation
                    await getOffers();
                } else {
                    throw new Error(`Failed to activate an offer, offerId: ${offer.offerId}`);
                }
            } catch (error) {
                SnackbarUtils.showPredefined('SERVER_ERROR');

                logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
            } finally {
                setActivatingOffers(prevOffers => {
                    const updatedSet = new Set(prevOffers);
                    updatedSet.delete(offer.offerId);
                    return updatedSet;
                });
            }
        }
    }

    function showSpinner(offer?: Offer) {
        return offer ? activatingOffers.has(offer.offerId) : false;
    }

    return {
        drawerOpen,
        handleActivateOffer,
        handleCardClick,
        handleCloseDrawer,
        isActivating,
        selectedOffer,
        setDrawerOpen,
        setSelectedOffer,
        showSpinner,
    };
}
